// File: /opt/dev_environment/assets/workspace/app-init/src/services/exportService.js

/**
 * Data Export Service
 * Now fetches data from the backend API export endpoint instead of localStorage.
 * CSV generation remains client-side for offline capability.
 * Includes Bearer token authentication.
 */

import { dataApi } from './apiClient';
import AuthService from './authService';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const EMAIL_ENDPOINT = `${API_BASE}/api/v1/export-email`;

const BOM = '\uFEFF';
const SEPARATOR = ';';

const VOCAB_HEADERS = [
  'Deutsch',
  'Italienisch',
  'Geschlecht',
  'Kategorie',
  'Thema',
  'Fach',
  'Erstellt',
  'Korrekt',
  'Falsch',
  'Fehlerquote',
];

const VERB_HEADERS = [
  'Verb',
  'Konjugationsform',
  'Person',
  'Deutsch',
  'Italienisch',
  'Fach',
  'Erstellt',
  'Korrekt',
  'Falsch',
  'Fehlerquote',
];

function escapeField(value) {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(SEPARATOR) || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function formatDate(isoString) {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function getCardStats(cardId, reviews) {
  const cardReviews = reviews.filter((r) => (r.cardId || r.card_id) === cardId);
  const total = cardReviews.length;
  if (total === 0) return { correct: 0, incorrect: 0, errorRate: '' };
  const incorrect = cardReviews.filter((r) => !r.correct).length;
  const correct = total - incorrect;
  const errorRate = `${Math.round((incorrect / total) * 100)}%`;
  return { correct, incorrect, errorRate };
}

function getDateStamp() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Generate vocabulary CSV from API-format data.
 */
function generateVocabularyCsvFromApi(vocabActive, vocabLearned, reviews) {
  const headerRow = VOCAB_HEADERS.map(escapeField).join(SEPARATOR);
  const rows = [headerRow];

  vocabActive.forEach((card) => {
    const stats = getCardStats(card.id, reviews);
    rows.push(
      [
        escapeField(card.german_word),
        escapeField(card.italian_word),
        escapeField(card.gender || ''),
        escapeField(card.category || ''),
        escapeField(card.topic_tag || ''),
        escapeField(card.compartment),
        escapeField(formatDate(card.created_at)),
        escapeField(stats.correct),
        escapeField(stats.incorrect),
        escapeField(stats.errorRate),
      ].join(SEPARATOR)
    );
  });

  vocabLearned.forEach((word) => {
    const stats = getCardStats(word.id, reviews);
    rows.push(
      [
        escapeField(word.german_word),
        escapeField(word.italian_word),
        escapeField(word.gender || ''),
        escapeField(word.category || ''),
        escapeField(word.topic_tag || ''),
        escapeField('Gelernt'),
        escapeField(formatDate(word.created_at)),
        escapeField(stats.correct),
        escapeField(stats.incorrect),
        escapeField(stats.errorRate),
      ].join(SEPARATOR)
    );
  });

  return BOM + rows.join('\r\n');
}

/**
 * Generate verb CSV from API-format data.
 */
function generateVerbCsvFromApi(verbsActive, verbsLearned, reviews) {
  const headerRow = VERB_HEADERS.map(escapeField).join(SEPARATOR);
  const rows = [headerRow];

  verbsActive.forEach((card) => {
    const stats = getCardStats(card.id, reviews);
    rows.push(
      [
        escapeField(card.base_verb),
        escapeField(card.conjugation_form_label || card.conjugation_form),
        escapeField(card.target_person_label || 'Participio Passato'),
        escapeField(card.german_conjugation || card.german_infinitive || ''),
        escapeField(card.conjugated_answer),
        escapeField(card.compartment),
        escapeField(formatDate(card.created_at)),
        escapeField(stats.correct),
        escapeField(stats.incorrect),
        escapeField(stats.errorRate),
      ].join(SEPARATOR)
    );
  });

  verbsLearned.forEach((verb) => {
    const stats = getCardStats(verb.id, reviews);
    rows.push(
      [
        escapeField(verb.base_verb),
        escapeField(verb.conjugation_form_label || verb.conjugation_form),
        escapeField(verb.target_person_label || 'Participio Passato'),
        escapeField(verb.german_conjugation || verb.german_infinitive || ''),
        escapeField(verb.conjugated_answer),
        escapeField('Gelernt'),
        escapeField(formatDate(verb.created_at)),
        escapeField(stats.correct),
        escapeField(stats.incorrect),
        escapeField(stats.errorRate),
      ].join(SEPARATOR)
    );
  });

  return BOM + rows.join('\r\n');
}

/**
 * Also keep the old functions for backward compatibility with components
 * that pass already-normalized frontend data.
 */
export function generateVocabularyCsv(cards, learnedWords, reviews) {
  const headerRow = VOCAB_HEADERS.map(escapeField).join(SEPARATOR);
  const rows = [headerRow];

  cards.forEach((card) => {
    const stats = getCardStats(card.id, reviews);
    rows.push(
      [
        escapeField(card.germanWord || card.german_word),
        escapeField(card.italianWord || card.italian_word),
        escapeField(card.gender || ''),
        escapeField(card.category || ''),
        escapeField(card.topicTag || card.topic_tag || ''),
        escapeField(card.compartment),
        escapeField(formatDate(card.createdAt || card.created_at)),
        escapeField(stats.correct),
        escapeField(stats.incorrect),
        escapeField(stats.errorRate),
      ].join(SEPARATOR)
    );
  });

  learnedWords.forEach((word) => {
    const stats = getCardStats(word.id, reviews);
    rows.push(
      [
        escapeField(word.germanWord || word.german_word),
        escapeField(word.italianWord || word.italian_word),
        escapeField(word.gender || ''),
        escapeField(word.category || ''),
        escapeField(word.topicTag || word.topic_tag || ''),
        escapeField('Gelernt'),
        escapeField(formatDate(word.createdAt || word.created_at)),
        escapeField(stats.correct),
        escapeField(stats.incorrect),
        escapeField(stats.errorRate),
      ].join(SEPARATOR)
    );
  });

  return BOM + rows.join('\r\n');
}

export function generateVerbCsv(verbCards, learnedVerbs, reviews) {
  const headerRow = VERB_HEADERS.map(escapeField).join(SEPARATOR);
  const rows = [headerRow];

  verbCards.forEach((card) => {
    const stats = getCardStats(card.id, reviews);
    rows.push(
      [
        escapeField(card.baseVerb || card.base_verb),
        escapeField(card.conjugationFormLabel || card.conjugation_form_label || card.conjugationForm || card.conjugation_form),
        escapeField(card.targetPersonLabel || card.target_person_label || 'Participio Passato'),
        escapeField(card.germanConjugation || card.german_conjugation || card.germanInfinitive || card.german_infinitive || ''),
        escapeField(card.conjugatedAnswer || card.conjugated_answer),
        escapeField(card.compartment),
        escapeField(formatDate(card.createdAt || card.created_at)),
        escapeField(stats.correct),
        escapeField(stats.incorrect),
        escapeField(stats.errorRate),
      ].join(SEPARATOR)
    );
  });

  learnedVerbs.forEach((verb) => {
    const stats = getCardStats(verb.id, reviews);
    rows.push(
      [
        escapeField(verb.baseVerb || verb.base_verb),
        escapeField(verb.conjugationFormLabel || verb.conjugation_form_label || verb.conjugationForm || verb.conjugation_form),
        escapeField(verb.targetPersonLabel || verb.target_person_label || 'Participio Passato'),
        escapeField(verb.germanConjugation || verb.german_conjugation || verb.germanInfinitive || verb.german_infinitive || ''),
        escapeField(verb.conjugatedAnswer || verb.conjugated_answer),
        escapeField('Gelernt'),
        escapeField(formatDate(verb.createdAt || verb.created_at)),
        escapeField(stats.correct),
        escapeField(stats.incorrect),
        escapeField(stats.errorRate),
      ].join(SEPARATOR)
    );
  });

  return BOM + rows.join('\r\n');
}

function downloadFile(content, filename, mimeType = 'text/csv;charset=utf-8') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Download CSV files — now fetches fresh data from the API export endpoint.
 */
export async function downloadCsvFiles(cards, learnedWords, verbCards, learnedVerbs, reviews) {
  try {
    const dateStamp = getDateStamp();

    // Try to fetch from API export endpoint for the most up-to-date data
    let vocabCsv, verbCsv;
    try {
      const exportData = await dataApi.exportAll();
      vocabCsv = generateVocabularyCsvFromApi(
        exportData.vocabulary_active || [],
        exportData.vocabulary_learned || [],
        exportData.reviews || []
      );
      verbCsv = generateVerbCsvFromApi(
        exportData.verbs_active || [],
        exportData.verbs_learned || [],
        exportData.reviews || []
      );
    } catch (apiError) {
      // Fallback to local state data if API is unavailable
      console.warn('[Export] API export failed, using local state:', apiError);
      vocabCsv = generateVocabularyCsv(cards, learnedWords, reviews);
      verbCsv = generateVerbCsv(verbCards, learnedVerbs, reviews);
    }

    downloadFile(vocabCsv, `vokabelkasten_vokabeln_${dateStamp}.csv`);

    setTimeout(() => {
      downloadFile(verbCsv, `vokabelkasten_verben_${dateStamp}.csv`);
    }, 300);

    return { success: true };
  } catch (error) {
    console.error('[Export] Download failed:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Send CSV files via email — now includes auth token.
 */
export async function sendCsvViaEmail(recipientEmail, data) {
  try {
    const dateStamp = getDateStamp();

    let vocabCsv, verbCsv;
    try {
      const exportData = await dataApi.exportAll();
      vocabCsv = generateVocabularyCsvFromApi(
        exportData.vocabulary_active || [],
        exportData.vocabulary_learned || [],
        exportData.reviews || []
      );
      verbCsv = generateVerbCsvFromApi(
        exportData.verbs_active || [],
        exportData.verbs_learned || [],
        exportData.reviews || []
      );
    } catch (apiError) {
      console.warn('[Export] API export failed, using local state:', apiError);
      vocabCsv = generateVocabularyCsv(data.cards, data.learnedWords, data.reviews);
      verbCsv = generateVerbCsv(data.verbCards, data.learnedVerbs, data.reviews);
    }

    // Build headers with auth token
    const headers = { 'Content-Type': 'application/json' };
    const accessToken = AuthService.getAccessToken();
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body: JSON.stringify({
        recipient_email: recipientEmail,
        date_stamp: dateStamp,
        vocabulary_csv: vocabCsv,
        verb_csv: verbCsv,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Server error: ${response.status}`);
    }

    const result = await response.json();
    return { success: true, message: result.message };
  } catch (error) {
    console.error('[Export] Email send failed:', error);
    return { success: false, error: error.message };
  }
}